import React from "react";
import { Link } from "react-router-dom";
import {
  useProfile,
  usePositionsData,
  useHasTransactions,
  useIntegrations,
  formatAmount,
  getCurrencySymbol,
} from "@moonscape/shared";
import { Skeleton } from "@/components/ui/skeleton";
import { Hero, Lab, Tag } from "@/components/ms";

export const PortfolioValueCard = () => {
  const { profile } = useProfile();
  const { data: positionsData, isLoading } = usePositionsData();
  const { data: hasTransactions, isLoading: isLoadingTxns } = useHasTransactions();
  const { data: integrationsData } = useIntegrations();

  // Extract balances from positions response
  const positions = positionsData?.balances || [];
  const positionsArray = Array.isArray(positions) ? positions : [];

  // Get base currency from profile
  const baseCurrency = profile?.baseCurrency || "USD";

  // Calculate metrics
  const portfolioValue = positionsArray.reduce((sum, p) => sum + (p.currentValue || 0), 0);
  const totalCostBasis = positionsArray.reduce((sum, p) => sum + (p.costBasis || 0), 0);
  const unrealizedPnL = portfolioValue - totalCostBasis;
  const unrealizedPnLPercent = totalCostBasis > 0 ? ((unrealizedPnL / totalCostBasis) * 100) : 0;

  // Count unique assets and exchanges
  const assetCount = positionsArray.length;
  const exchangeCount = new Set(positionsArray.map((p) => p.exchange).filter(Boolean)).size;

  // Format currency
  const formatCurrency = (amount: number) => {
    const symbol = getCurrencySymbol(baseCurrency);
    return `${symbol}${formatAmount(amount)}`;
  };

  // Check if any sync is in progress
  const hasSyncInProgress = integrationsData?.items?.some(
    (integration) =>
      integration.sync_state === 'starting' ||
      integration.sync_state === 'in_progress'
  ) || false;

  // Hide widget if user has transactions but no balances (avoid misleading CTA)
  // BUT don't hide if sync is in progress (balances may be loading)
  const hasBalances = positionsArray.length > 0;
  if (!isLoading && !isLoadingTxns && !hasBalances && hasTransactions && !hasSyncInProgress) {
    return null;
  }

  if (isLoading || isLoadingTxns) {
    return (
      <section className="flex flex-col">
        <Skeleton className="h-3 w-40" />
        <Skeleton className="mt-3 h-12 w-64" />
        <Skeleton className="mt-4 h-5 w-52" />
      </section>
    );
  }

  return (
    <section className="flex flex-col">
      <Link to="/portfolio" className="block transition-colors hover:text-ms-green">
        <Hero
          label="Portfolio value · all sources"
          value={formatCurrency(portfolioValue)}
          meta={
            <>
              {totalCostBasis > 0 && (
                <Tag tone={unrealizedPnL >= 0 ? "pos" : "neg"}>
                  {unrealizedPnL >= 0 ? "+" : ""}
                  {unrealizedPnLPercent.toFixed(1)}% · {unrealizedPnL >= 0 ? "+" : ""}
                  {formatCurrency(unrealizedPnL)}
                </Tag>
              )}
              {exchangeCount > 0 && (
                <Tag tone="live">
                  {hasSyncInProgress ? "Syncing" : "Live"} · {exchangeCount} source
                  {exchangeCount !== 1 ? "s" : ""}
                </Tag>
              )}
              <Lab className="text-ms-dim">
                {assetCount} asset{assetCount !== 1 ? "s" : ""}
                {totalCostBasis > 0 ? ` · cost basis ${formatCurrency(totalCostBasis)}` : ""}
              </Lab>
            </>
          }
        />
      </Link>
    </section>
  );
};
